import { ProjectModel } from "@/types/models";
import { privateApi } from "./api";
import { getToken } from "./auth.service";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from "react-router-dom";

const getProjects = async () => {
    return await privateApi.get<ProjectModel[]>('/project');
};

const getProject = async (projectId: string) => {
    return await privateApi.get<ProjectModel>(`/project/${projectId}`);
};

const updateProject = async ({ projectId, data }: { projectId: string; data: { name?: string; description?: string } }) => {
    return await privateApi.patch<ProjectModel>(`/project/${projectId}`, data);
};

const deleteProject = async (projectId: string) => {
    await privateApi.delete(`/project/${projectId}`);
};

const restoreProject = async (projectId: string) => {
    await privateApi.patch(`/project/${projectId}/restore`);
};

// Hooks
export const useProjects = () => {
    return useQuery({
        queryKey: ['projects'],
        queryFn: () => getProjects().then((response) => response.data),
        staleTime: 1000 * 60 * 5,
        enabled: !!getToken(),
    });
};

export const useProject = (projectId?: string) => {
    return useQuery({
        queryKey: ['project', projectId],
        queryFn: () => {
            if (!projectId) return Promise.reject("Project ID missing");
            return getProject(projectId).then((response) => response.data);
        },
        enabled: !!projectId,
    });
};

export const useUpdateProject = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: updateProject,
        onSuccess: (_, { projectId }) => {
            queryClient.invalidateQueries({ queryKey: ['projects'] });
            queryClient.invalidateQueries({ queryKey: ['project', projectId] });
        },
        onError: (error) => {
            console.error("Error while updating project", error);
            throw error;
        },
    });
};

// Super admins only: brings a deleted project back to its members.
export const useRestoreProject = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: restoreProject,
        onSuccess: (_, projectId) => {
            queryClient.invalidateQueries({ queryKey: ['projects'] });
            queryClient.invalidateQueries({ queryKey: ['project', projectId] });
        },
    });
};

export const useDeleteProject = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteProject,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['projects'] });
            navigate('/project');
        },
        onError: (error) => {
            console.error("Error while deleting project", error);
            throw error;
        },
    });
};